<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const faq = [
  {
    q: 'Staff lupa PIN-nya, gimana caranya reset?',
    a: 'Buka User & Role → Users, klik ikon sidik jari ("Detail Absensi") di baris staff tersebut, lalu isi PIN baru di form "Reset PIN". Tidak perlu tahu PIN lama — ini aksi reset oleh admin, bukan ganti PIN oleh staff sendiri.',
  },
  {
    q: 'QR fisik yang ditempel/dibawa staff hilang atau rusak, perlu bikin ulang?',
    a: 'Tidak perlu bikin ulang dari nol. Buka Detail Absensi staff tersebut, QR yang sama (kode di baliknya tidak berubah) akan tetap tampil — tinggal klik "Unduh QR" atau "Cetak QR" lagi untuk mencetak fisiknya.',
  },
  {
    q: 'Kenapa staff tertentu tidak pernah muncul status "Terlambat" atau "Tepat Waktu" di laporan, selalu kosong?',
    a: 'Kemungkinan besar jadwal kerja staff itu belum diatur di menu Jadwal Kerja Staff. Tanpa jadwal, sistem tidak tahu jam berapa staff itu seharusnya masuk, jadi status keterlambatannya tidak dihitung sama sekali (bukan error).',
  },
  {
    q: 'Bisa aktifkan cek lokasi (geofence) tanpa mewajibkan foto verifikasi, atau sebaliknya?',
    a: 'Bisa. Ketiganya — Aktifkan Fitur Absensi, Wajib Absen Sebelum Buka Shift, dan Aktifkan Foto Verifikasi — adalah 3 toggle terpisah di Pengaturan → tab Absensi. Anda bisa menyalakan lokasi tanpa foto, foto tanpa syarat wajib-absen-sebelum-shift, atau kombinasi apa pun sesuai kebutuhan toko.',
  },
  {
    q: 'Kenapa staff yang PIN-nya benar tetap ditolak saat absen?',
    a: 'Cek dulu apakah staff itu berstatus Aktif di menu User & Role — staff nonaktif tidak bisa absen sama sekali walau PIN/QR-nya benar. Kemungkinan lain: sudah kena batas percobaan salah (5x salah dalam 1 menit akan diblokir sementara), coba lagi setelah beberapa saat.',
  },
  {
    q: 'Apakah dua staff bisa absen dari QR/PIN yang sama secara bersamaan?',
    a: 'Tidak — setiap PIN dan setiap QR selalu unik untuk satu staff. Kalau Anda mencoba mengatur PIN yang sudah dipakai staff aktif lain, sistem otomatis menolak dengan pesan jelas (lihat bagian "Mengatur PIN & QR Karyawan" di atas).',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Absensi Karyawan</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            size="small"
            class="mb-4"
          >
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="64"
            rounded="lg"
            class="me-4"
          >
            <VIcon
              icon="ri-fingerprint-line"
              size="36"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Absensi Karyawan
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              PIN, QR, lokasi toko, dan laporan kehadiran — lengkap dari persiapan sampai penggajian
            </p>
          </div>
        </div>
        <VAlert
          type="info"
          variant="tonal"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda dari nol — mengatur lokasi toko &amp; jadwal staff, mengatur PIN/QR tiap
          karyawan, cara karyawan absen di Kiosk, sampai membaca dan memverifikasi Laporan
          Kehadiran untuk hitung gaji.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Alur singkat -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert
          type="success"
          variant="tonal"
        >
          <strong>Alur singkatnya:</strong>
          Atur lokasi toko &amp; jadwal staff → Atur PIN/QR tiap staff → Staff absen di Kiosk
          (PIN/QR + izin lokasi + foto kalau diaktifkan) → Laporan Kehadiran otomatis terisi,
          tinggal verifikasi &amp; export untuk penggajian.
        </VAlert>
      </VCol>
    </VRow>

    <!-- 1. Persiapan Awal -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            1
          </VChip>
          Persiapan Awal
        </h5>
        <VCard class="mb-4">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-map-pin-line"
              class="me-2"
              color="primary"
            />
            Atur Lokasi Toko &amp; Radius Geofence
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Buka menu <strong>Pengaturan</strong> di sidebar kiri, lalu pindah ke tab
                <strong>"Absensi"</strong>.
              </li>
              <li class="mb-2">
                Nyalakan toggle <strong>"Aktifkan Fitur Absensi"</strong> — kalau toggle ini
                mati, seluruh fitur absensi tidak berpengaruh apa pun ke alur POS/shift yang
                sudah ada.
              </li>
              <li class="mb-2">
                Isi <strong>Latitude</strong> dan <strong>Longitude</strong> lokasi toko —
                paling mudah klik tombol <strong>"Ambil Lokasi Saat Ini"</strong> saat Anda
                sedang berada di toko (browser akan minta izin GPS).
              </li>
              <li class="mb-2">
                Atur <strong>Radius Geofence (meter)</strong> — jarak maksimal dari titik toko
                yang masih dianggap "di toko", dan <strong>Toleransi Keterlambatan (menit)</strong>
                — berapa menit setelah jadwal masih dianggap tepat waktu.
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan Pengaturan Absensi"</strong>.
              </li>
            </ol>
            <VAlert
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-0 text-body-2"
            >
              Kalau Latitude/Longitude dibiarkan kosong, validasi lokasi otomatis
              <strong>DILEWATI</strong> — staff bisa absen dari mana saja tanpa dicek lokasinya.
              Isi keduanya kalau Anda memang ingin memastikan staff absen dari toko.
            </VAlert>
          </VCardText>
        </VCard>

        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-calendar-check-line"
              class="me-2"
              color="info"
            />
            Atur Jadwal Kerja per Staff
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Buka menu <strong>SDM &amp; Absensi → Jadwal Kerja Staff</strong> di sidebar kiri.
              </li>
              <li class="mb-2">
                Pilih nama staff dari dropdown <strong>"Pilih Staff"</strong> — grid 7 hari
                langsung muncul.
              </li>
              <li class="mb-2">
                Untuk tiap hari: nyalakan/matikan toggle <strong>"Hari Kerja"</strong>, lalu isi
                <strong>Jam Masuk</strong> dan <strong>Jam Pulang</strong> untuk hari yang aktif.
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan Jadwal"</strong>. Ulangi untuk staff lainnya (pilih nama
                lain di dropdown yang sama).
              </li>
            </ol>
            <VAlert
              type="info"
              variant="tonal"
              density="compact"
              class="mb-0 text-body-2"
            >
              Jadwal ini dipakai untuk menghitung status Terlambat/Tepat Waktu di laporan. Staff
              yang belum diatur jadwalnya tetap bisa absen normal — statusnya cuma akan tampil
              "Tidak Dijadwalkan" (bukan error), bukan Terlambat atau Tepat Waktu.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 2. PIN & QR -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            2
          </VChip>
          Mengatur PIN &amp; QR Karyawan
        </h5>
        <VCard>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Buka menu <strong>User &amp; Role → Users</strong> di sidebar kiri.
              </li>
              <li class="mb-2">
                Di baris staff yang ingin diatur, klik ikon <strong>sidik jari</strong>
                (tooltip <em>"Detail Absensi"</em>) — tombol ini hanya muncul untuk akun
                dengan izin mengatur absensi (biasanya Owner).
              </li>
              <li class="mb-2">
                Di dialog <strong>"Detail Absensi — {nama staff}"</strong> yang terbuka, isi
                <strong>PIN Baru</strong> (4-6 digit angka) dan <strong>Konfirmasi PIN</strong>,
                lalu klik <strong>"Simpan PIN"</strong> (atau <strong>"Reset PIN"</strong> kalau
                staff itu sudah pernah punya PIN sebelumnya).
              </li>
              <li class="mb-0">
                QR Code otomatis dibuat di bagian bawah dialog. Klik <strong>"Unduh QR"</strong>
                untuk simpan gambarnya, atau <strong>"Cetak QR"</strong> untuk langsung cetak
                (mis. untuk ditempel di kartu nama/lanyard staff).
              </li>
            </ol>

            <VAlert
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              <strong>🔒 PIN Wajib Unik.</strong> Sistem absen mencocokkan PIN yang diketik ke
              SEMUA staff (bukan pilih nama dulu, baru masukkan PIN) — jadi setiap PIN
              <strong>harus beda</strong> antar staff yang masih aktif. Kalau Anda mencoba
              mengisi PIN yang sudah dipakai staff aktif lain, sistem langsung menolak dengan
              pesan <em>"PIN ini sudah dipakai staff lain, gunakan PIN berbeda."</em> — pilih
              angka lain.
            </VAlert>

            <p class="text-body-1 font-weight-medium mb-2">
              <VIcon
                icon="ri-mail-send-line"
                size="18"
                class="me-1"
              />
              Kirim QR &amp; Link Atur PIN Sendiri via Email
            </p>
            <p class="text-body-2 mb-2">
              Daripada Anda yang mengatur PIN staff satu-satu, di dialog yang sama ada tombol
              <strong>"Kirim via Email"</strong> — mengirim link ke email staff supaya dia bisa
              atur PIN-nya sendiri dan langsung dapat QR-nya sendiri, tanpa perlu Anda ketikkan.
            </p>
            <VAlert
              type="error"
              variant="tonal"
              density="compact"
              class="mb-0 text-body-2"
            >
              <strong>⏱️ Batas waktu link:</strong> link ini cuma berlaku maksimal
              <strong>1x24 jam</strong> dan hanya bisa dipakai <strong>satu kali</strong>. Kalau
              staff belum sempat membukanya dalam waktu itu, linknya kedaluwarsa — klik tombol
              "Kirim via Email" lagi untuk mengirim link baru (link lama otomatis tidak berlaku
              lagi begitu Anda kirim ulang).
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 3. Cara Absen di Kiosk -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            3
          </VChip>
          Cara Karyawan Absen di Kiosk
        </h5>
        <VCard>
          <VCardText>
            <p class="text-body-2 mb-3">
              Buka menu <strong>SDM &amp; Absensi → Absen Karyawan (Kiosk)</strong> di perangkat
              (tablet/HP) yang diletakkan di dekat pintu masuk atau meja kasir. Layar ini
              sengaja <strong>netral</strong> — tidak menampilkan siapa yang sedang login di
              device tersebut, supaya bisa dipakai gantian oleh siapa saja yang mau absen.
            </p>

            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Staff pilih salah satu dari 2 tombol besar: <strong>"Input PIN"</strong>
                (keypad angka) atau <strong>"Scan QR"</strong> (buka kamera, foto kode QR-nya).
              </li>
              <li class="mb-2">
                Browser akan minta <strong>izin lokasi (GPS)</strong> — staff harus
                <strong>izinkan</strong> supaya sistem bisa mengecek apakah dia sedang berada
                di area toko (kalau lokasi toko sudah diatur di Langkah 1).
              </li>
              <li class="mb-2">
                Kalau toggle <strong>"Aktifkan Foto Verifikasi Saat Absen"</strong> aktif, layar
                <strong>"Ambil Foto"</strong> muncul setelah PIN/QR cocok — staff ambil 1 foto
                lewat kamera, otomatis dikompresi kecil oleh sistem, lalu terkirim bersama
                data absennya.
              </li>
              <li class="mb-0">
                Layar hasil menampilkan <strong>nama staff</strong>, status
                <strong>Clock-in/Clock-out berhasil</strong>, dan status
                <strong>Tepat Waktu/Terlambat</strong> (kalau jadwalnya sudah diatur).
              </li>
            </ol>

            <VAlert
              type="info"
              variant="tonal"
              class="mb-3"
            >
              <strong>Tidak perlu pilih nama dulu.</strong> PIN atau kode QR itu sendiri yang
              menjadi identitas staff — begitu PIN/QR cocok, sistem langsung tahu siapa yang
              sedang absen tanpa langkah tambahan.
            </VAlert>

            <p class="text-body-2 mb-0">
              <strong>Absen bolak-balik dalam sehari:</strong> kalau staff sudah clock-in lalu
              absen lagi di hari yang sama, otomatis dianggap <strong>clock-out</strong>. Kalau
              sudah clock-out lalu absen lagi, otomatis dianggap <strong>clock-in baru</strong>
              (mis. staff balik lagi setelah istirahat) — bukan error.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 4. Lokasi ditolak -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="warning"
            size="small"
            class="me-2"
          >
            4
          </VChip>
          Kalau Lokasi Ditolak (Di Luar Radius)
        </h5>
        <VCard>
          <VCardText>
            <p class="text-body-2 mb-3">
              Kalau staff absen dari luar radius yang Anda atur (atau GPS-nya gagal/ditolak),
              layar Kiosk menampilkan <strong>"Di Luar Jangkauan Toko"</strong> — absennya
              <strong>belum tercatat</strong>.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Staff klik tombol <strong>"Minta Persetujuan Manager/Owner"</strong>.
              </li>
              <li class="mb-2">
                Layar PIN <strong>kedua</strong> muncul — kali ini untuk PIN
                <strong>Manager/Owner</strong> yang sedang ada di lokasi (bukan PIN staff yang
                absen tadi).
              </li>
              <li class="mb-0">
                Manager/Owner mengetik PIN-nya sendiri untuk menyetujui. Kalau valid, absen
                staff tadi <strong>tetap tercatat</strong>, ditandai <strong>Override</strong>
                di Laporan Kehadiran supaya transparan buat Anda kalau ada absen yang
                "dilonggarkan".
              </li>
            </ol>
            <VAlert
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-0 text-body-2"
            >
              Hanya PIN milik user yang punya izin approve (Owner/Manager) yang diterima di
              layar ini — PIN staff biasa akan ditolak di sini walau PIN-nya sendiri benar.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 5. Syarat buka shift -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            5
          </VChip>
          Syarat Absen Sebelum Buka Shift Kasir (Opsional)
        </h5>
        <VCard>
          <VCardText>
            <p class="text-body-2 mb-3">
              Di Pengaturan → tab Absensi ada toggle terpisah <strong>"Wajib Absen Sebelum
                Buka Shift Kasir"</strong> — ini opsional, tidak ikut menyala otomatis walau
              fitur absensi sudah aktif.
            </p>
            <VRow>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="success"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VChip
                      color="success"
                      size="small"
                      class="mb-2"
                    >
                      Toggle Nonaktif
                    </VChip>
                    <p class="text-body-2 mb-0">
                      Alur buka shift kasir berjalan <strong>persis seperti biasa</strong>,
                      tanpa syarat absen apa pun — kasir langsung isi kas awal seperti sebelum
                      fitur ini ada.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="warning"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VChip
                      color="warning"
                      size="small"
                      class="mb-2"
                    >
                      Toggle Aktif
                    </VChip>
                    <p class="text-body-2 mb-0">
                      Kasir <strong>wajib clock-in dulu</strong> di Kiosk sebelum bisa membuka
                      shift POS. Kalau belum absen, sistem mengarahkan ke Kiosk terlebih dahulu.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 6. Laporan Kehadiran -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            6
          </VChip>
          Laporan Kehadiran
        </h5>
        <VCard>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Buka menu <strong>SDM &amp; Absensi → Laporan Kehadiran</strong> di sidebar kiri.
              </li>
              <li class="mb-2">
                Atur filter <strong>Dari Tanggal</strong>, <strong>Sampai Tanggal</strong>, dan
                (opsional) <strong>Staff</strong> tertentu, lalu klik <strong>"Cari"</strong>.
                Ringkasan di atas tabel otomatis menampilkan Total Absen, Tepat Waktu,
                Terlambat, Override, dan Total Durasi.
              </li>
              <li class="mb-2">
                Tabel detail menampilkan tiap absen: Staff, Jam Masuk, Jam Keluar, Durasi,
                Status, Override, <strong>Foto</strong>, dan <strong>Terverifikasi</strong>.
              </li>
              <li class="mb-2">
                Kolom <strong>Foto</strong>: klik thumbnail kecilnya untuk membuka foto ukuran
                penuh (foto clock-in dan clock-out ditampilkan terpisah kalau keduanya ada).
              </li>
              <li class="mb-0">
                Kolom <strong>Terverifikasi</strong>: centang checkbox satu per satu untuk
                verifikasi individual, atau klik tombol <strong>"Centang Semua"</strong> lalu
                <strong>"Verifikasi Terpilih"</strong> untuk verifikasi sekaligus.
              </li>
            </ol>

            <VAlert
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3 text-body-2"
            >
              <strong>"Centang Semua" hanya menandai baris yang SEDANG TAMPIL</strong> di
              halaman tabel itu (mis. 10 baris pertama) — bukan seluruh data hasil filter,
              supaya Anda tidak tidak sengaja menandai ribuan baris sekaligus. Baris yang sudah
              diverifikasi checkbox-nya otomatis terkunci tercentang (tidak bisa dibatalkan) —
              ini catatan audit satu arah.
            </VAlert>

            <p class="text-body-2 mb-0">
              Untuk keperluan penggajian, klik tombol <strong>"Excel"</strong> atau
              <strong>"CSV"</strong> di pojok kanan atas untuk mengunduh rekap sesuai filter
              yang sedang aktif.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 7. Foto & Retensi -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            7
          </VChip>
          Soal Foto &amp; Retensi Data
        </h5>
        <VCard
          color="warning"
          variant="tonal"
        >
          <VCardText>
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VCard
                  variant="outlined"
                  class="h-100"
                >
                  <VCardText>
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon
                        icon="ri-time-line"
                        size="20"
                      />
                      <strong class="text-body-1">Hari ke-30: Peringatan</strong>
                    </div>
                    <p class="text-body-2 mb-0">
                      Anda dapat notifikasi email + notifikasi di aplikasi: foto absen tanggal
                      tertentu akan dihapus otomatis 10 hari lagi.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VCard
                  variant="outlined"
                  class="h-100"
                >
                  <VCardText>
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon
                        icon="ri-delete-bin-line"
                        size="20"
                      />
                      <strong class="text-body-1">Hari ke-40: Foto Terhapus</strong>
                    </div>
                    <p class="text-body-2 mb-0">
                      File fotonya benar-benar dihapus dari server — kolom Foto di laporan
                      untuk absen tersebut jadi kosong.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
            <VAlert
              type="success"
              variant="tonal"
              class="mt-4 mb-0"
            >
              <strong>✅ Yang PENTING diketahui:</strong> data <strong>jam masuk/keluar,
                status Terlambat, dan Override TIDAK PERNAH ikut terhapus</strong> bersama
              fotonya — semua tetap ada selamanya untuk keperluan hitung gaji, walau fotonya
              sudah hilang. Foto cuma alat bantu verifikasi visual sementara, bukan bagian dari
              data kehadiran inti.
            </VAlert>
          </VCardText>
        </VCard>

        <VCard class="mt-4">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-archive-line"
              class="me-2"
              color="secondary"
            />
            Reset Data Tahunan (Otomatis Setiap 1 Januari)
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Setiap tanggal 1 Januari, sistem otomatis memproses data absensi
              <strong>tahun sebelumnya</strong>:
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Seluruh data absensi tahun itu <strong>diarsipkan dulu ke file</strong>
                (tersimpan aman di server, bukan link publik yang bisa diakses sembarang orang)
                — untuk keperluan sengketa gaji atau audit di kemudian hari.
              </li>
              <li class="mb-0">
                Setelah arsip berhasil dibuat, tampilan <strong>Laporan Kehadiran dibersihkan</strong>
                dari data tahun itu — data absensi mulai dari 0 lagi untuk tahun berjalan. Anda
                dapat notifikasi begitu proses ini selesai.
              </li>
            </ol>
            <VAlert
              type="info"
              variant="tonal"
              density="compact"
              class="mb-0 text-body-2"
            >
              Toko yang tidak pernah mengaktifkan/memakai fitur absensi sepanjang tahun itu
              tidak ikut diproses sama sekali — tidak ada yang perlu diarsipkan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 8. Batasan -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="error"
            size="small"
            class="me-2"
          >
            8
          </VChip>
          Batasan yang Perlu Anda Ketahui
        </h5>
        <VAlert
          type="error"
          variant="tonal"
          icon="ri-shield-cross-line"
        >
          <p class="text-body-2 mb-2">
            <strong>PIN &amp; QR bukan biometrik.</strong> Kalau seorang staff menitipkan
            PIN/QR-nya ke orang lain (sama seperti titip kartu absen fisik biasa), sistem akan
            tetap mencatat absen itu sebagai identitas pemilik PIN/QR tersebut — bukan siapa
            yang sesungguhnya memegang HP/kartu. Ini batasan bawaan sistem berbasis PIN/QR,
            bukan sesuatu yang bisa "diperbaiki" penuh lewat aplikasi.
          </p>
          <p class="text-body-2 mb-2">
            <strong>Foto verifikasi bukan penolakan otomatis.</strong> Sistem
            <strong>tidak</strong> mencocokkan wajah di foto secara otomatis — foto hanya
            tersimpan sebagai bukti visual yang bisa Anda buka manual di Laporan Kehadiran
            kalau ada kecurigaan (mis. cek apakah orangnya sesuai). Absen tetap tercatat
            walau fotonya buram atau kurang jelas.
          </p>
          <p class="text-body-2 mb-0">
            <strong>Lokasi GPS bisa dipalsukan</strong> di HP yang sudah dimodifikasi khusus
            (root/aplikasi GPS palsu) — ini keterbatasan bawaan teknologi GPS berbasis
            browser. Fitur "Minta Persetujuan Manager/Owner" (Langkah 4) memang sengaja
            disediakan sebagai solusi sah untuk GPS yang kurang akurat (mis. di dalam
            ruangan), tapi ini juga berarti keamanannya jadi bergantung pada kedisiplinan
            Manager/Owner untuk tidak asal menyetujui — ini kebijakan operasional toko Anda,
            bukan sesuatu yang bisa dikunci penuh dari sisi sistem.
          </p>
        </VAlert>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'User & Role', sub: 'Tambah karyawan & atur hak akses', icon: 'ri-group-line', color: 'info', to: '/userguide/sistem/user-role' },
              { title: 'Pengaturan Toko', sub: 'Konfigurasi umum, pajak, & preferensi', icon: 'ri-settings-3-line', color: 'primary', to: '/userguide/sistem/pengaturan' },
              { title: 'POS Kasir', sub: 'Alur buka shift & transaksi harian', icon: 'ri-shopping-cart-line', color: 'success', to: '/userguide/operasional/pos-kasir' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>